/**
 * Reparto del gasto por modelo: tokens, costo y participación.
 *
 * Contesta "¿qué modelos se llevan el gasto?", y sobre todo si uno caro se lo
 * lleva **sin justificarlo**: por eso la tabla pone la participación del costo al
 * lado de la de tokens. Un modelo con 27% de los tokens y 57% del gasto es la
 * fila que hay que mirar, y esa lectura solo existe si los dos porcentajes están
 * en la misma fila.
 *
 * Dos cosas que este bloque tiene que declarar y no dar por sabidas:
 *
 *  - **El costo es estimado por tarifa de catálogo, no facturado.** Es el riesgo
 *    que el spec anticipa: el número de Langfuse puede no coincidir con la
 *    factura del proveedor. Así que las dos tarifas viajan en la fila —USD por
 *    millón de tokens, entrada y salida— y el pie lo dice con todas las letras.
 *    Sin la tarifa cargada el costo no es auditable, y la celda lo muestra como
 *    dato faltante con su acción en vez de esconderlo.
 *  - **La participación publica su base.** `participacionCosto.base` trae parte y
 *    total, así que el "56,9%" se imprime con "$2.133,74 de $3.751,75" al lado.
 *
 * La barra de participación va en severidad neutra a propósito. Un modelo no está
 * "bien" ni "mal" por llevarse una porción del gasto: la barra mide magnitud, y
 * pintarla de verde o rojo le pondría un juicio que el dato no tiene. El
 * vocabulario de color del panel significa estado, no categoría.
 */
import type { Links, ModelUsage } from '@ops/contracts'
import { formatMetricValue } from '@ops/contracts'

import {
  DataTable,
  MissingCell,
  Panel,
  ProgressBar,
  type Column,
} from '../../ui'
import { Vacio } from '../comunes'
import { formatUsdUnitario } from './derivar'
import { LinkLangfuse } from './LinkLangfuse'

export interface PorModeloProps {
  modelos: readonly ModelUsage[]
  links: Links
  id?: string
}

export function PorModelo({ modelos, links, id }: PorModeloProps) {
  const columnas: ReadonlyArray<Column<ModelUsage>> = [
    {
      key: 'modelo',
      header: 'Modelo',
      rowHeader: true,
      width: '190px',
      sortValue: (m) => m.model.nombre,
      filterValue: (m) => `${m.model.nombre} ${m.model.proveedor}`,
      render: (m) => (
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate">{m.model.nombre}</span>
          <span className="text-micro font-normal text-muted-strong">
            {m.model.proveedor}
            {m.model.activo ? '' : ' · dado de baja del catálogo'}
          </span>
        </span>
      ),
    },
    {
      key: 'participacion',
      header: 'Participación del gasto',
      width: '250px',
      sortValue: (m) => m.participacionCosto?.pct ?? null,
      render: (m) =>
        m.participacionCosto === null ? (
          /* Participación sobre un total en cero no es 0%: es indefinida. */
          <MissingCell que="costo total del período" />
        ) : (
          <span className="flex flex-col gap-1">
            <ProgressBar
              pct={m.participacionCosto.pct}
              label={`Participación de ${m.model.nombre} en el costo del período`}
              severidad="unknown"
              etiqueta={formatMetricValue(m.participacionCosto.pct, 'percent')}
              size="sm"
            />
            {/* La base de la regla 4: parte y total, para rehacer el porcentaje. */}
            <span
              data-testid="participacion-base"
              className="tabular text-micro text-muted-strong"
            >
              {formatMetricValue(m.participacionCosto.base.parte, 'usd')} de{' '}
              {formatMetricValue(m.participacionCosto.base.total, 'usd')}
            </span>
          </span>
        ),
    },
    {
      key: 'costo',
      header: 'Costo estimado',
      align: 'right',
      width: '140px',
      sortValue: (m) => m.costoUsd,
      render: (m) => (
        <span className="tabular font-semibold">
          {formatMetricValue(m.costoUsd, 'usd')}
        </span>
      ),
    },
    {
      key: 'tokens',
      header: 'Tokens entrada · salida',
      align: 'right',
      width: '210px',
      sortValue: (m) => m.inputTokens + m.outputTokens,
      render: (m) => (
        <span className="flex flex-col items-end gap-0.5">
          <span className="tabular">
            {formatMetricValue(m.inputTokens, 'count')} ·{' '}
            {formatMetricValue(m.outputTokens, 'count')}
          </span>
          {m.participacionTokens === null ? null : (
            <span className="tabular text-micro text-muted-strong">
              {formatMetricValue(m.participacionTokens.pct, 'percent')} de{' '}
              {formatMetricValue(m.participacionTokens.base.total, 'count')}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'trazas',
      header: 'Trazas · costo por traza',
      align: 'right',
      width: '190px',
      sortValue: (m) => m.trazas,
      render: (m) => (
        <span className="tabular">
          {formatMetricValue(m.trazas, 'count')}
          <span className="text-micro text-muted-strong">
            {' · '}
            {m.costoPorTraza === null
              ? 'sin trazas'
              : formatUsdUnitario(m.costoPorTraza)}
          </span>
        </span>
      ),
    },
    {
      key: 'tarifa',
      header: 'Tarifa por millón',
      align: 'right',
      width: '180px',
      sortValue: (m) => m.model.precioInputPorMillon,
      render: (m) =>
        m.model.precioInputPorMillon === null &&
        m.model.precioOutputPorMillon === null ? (
          /* Sin tarifa el costo no se puede auditar: es un dato que falta, con
             su acción, no una celda vacía. */
          <MissingCell que="tarifa del modelo" />
        ) : (
          <span className="tabular text-micro text-muted-strong">
            {m.model.precioInputPorMillon === null
              ? 'sin dato'
              : formatMetricValue(m.model.precioInputPorMillon, 'usd')}{' '}
            entrada ·{' '}
            {m.model.precioOutputPorMillon === null
              ? 'sin dato'
              : formatMetricValue(m.model.precioOutputPorMillon, 'usd')}{' '}
            salida
          </span>
        ),
    },
    {
      key: 'langfuse',
      header: 'Uso en Langfuse',
      width: '260px',
      render: (m) => (
        <LinkLangfuse
          sobre={links.modelos[m.model.id]}
          texto="Ver el uso del modelo"
        />
      ),
    },
  ]

  if (modelos.length === 0) {
    return (
      <div id={id} className="min-w-0">
        <Panel titulo="Reparto por modelo" padding="sm">
          <Vacio
            icono={null}
            punteada
            mensaje="Ningún modelo registró consumo en el período"
            detalle="El reparto lo arma Langfuse por modelo. Sin trazas medidas no hay filas, que no es lo mismo que un catálogo en cero."
          />
        </Panel>
      </div>
    )
  }

  return (
    <div id={id} className="min-w-0">
      <Panel
        titulo="Reparto por modelo"
        subtitulo="Cada fila lleva su participación del gasto y su participación de tokens: un modelo que se lleva más gasto que tokens es más caro por token, y eso es una decisión, no un accidente."
        padding="none"
        footer={
          <>
            El costo es <b>estimado con la tarifa de catálogo</b>, no facturado
            por el proveedor: puede no coincidir con la factura. Las dos tarifas
            usadas están en la fila, y se administran en Admin → fuentes de datos.
          </>
        }
      >
        <DataTable
          columns={columnas}
          rows={modelos}
          rowKey={(m) => m.model.id}
          density="compacta"
          ordenInicial={{ key: 'costo', dir: 'desc' }}
          filtroPlaceholder="modelo o proveedor"
          emptyMessage="Ningún modelo con consumo en el período"
          caption="Reparto del gasto por modelo en el período: tokens, trazas, costo estimado por tarifa de catálogo y participación."
        />
      </Panel>
    </div>
  )
}
